<script setup lang="ts">
  import { isSameDay, setHours, setMinutes } from 'date-fns'
  import { computed, ref, watch } from 'vue'
  import { fmt } from '@/functions/index.ts'
  import { useAppModal } from '../modal'
  import DatepickerNavigator from './datepicker-navigator.vue'
  import ViewDaysInMonth from './view-days-in-month.vue'
  import ViewMonthsInYear from './view-months-in-year.vue'

  defineProps<{
    disabledDate?: (date: Date) => boolean,
    clearable?: boolean,
  }>()
  const model = defineModel<Date | null>({ required: true })
  const modelInternal = ref<Date | null>(null)

  const view = ref('days')
  const month = ref<number>((model.value ?? new Date()).getMonth())
  const year = ref<number>((model.value ?? new Date()).getFullYear())

  const { modal, open, close } = useAppModal(() => {
    // Reset the date picker to the current month and year
    modelInternal.value = model.value
  })
  const dateClicked = (date: Date) => {
    modelInternal.value = date
  }
  const confirm = () => {
    model.value = modelInternal.value
    close()
  }

  watch(modelInternal, (date) => {
    if (date) {
      month.value = date.getMonth()
      year.value = date.getFullYear()
    }
  })
  const hour = computed({
    get: () => modelInternal.value?.getHours() ?? 0,
    set (value: number) {
      if (modelInternal.value) {
        modelInternal.value = setHours(new Date(modelInternal.value), value)
      }
    },
  })
  const minute = computed({
    get: () => modelInternal.value?.getMinutes() ?? 0,
    set (value: number) {
      if (modelInternal.value) {
        modelInternal.value = setMinutes(new Date(modelInternal.value), value)
      }
    },
  })

  const FORMAT_DATE = 'dd MMM yyyy'
  const stringifiedDate = computed(() => {
    if (modelInternal.value) {
      return fmt.date(modelInternal.value, FORMAT_DATE)
    }
    return ''
  })
  const clearDate = () => {
    model.value = null
  }
</script>

<template>
  <div>
    <app-input
      label="Filter Tanggal"
      class="w-33"
      :model-value="stringifiedDate ? stringifiedDate : 'Pilih Tanggal'"
      readonly>
      <template #inline>
        <div
          class="absolute inset-0 z-0"
          @click="open()"></div>
        <app-button
          v-if="stringifiedDate && clearable"
          class="absolute z-1 right-1 top-1/2 -translate-y-1/2 text-2.5 size-5 flex-center rounded-full hover:bg-gray-200"
          @click="clearDate()">
          <fa-icon icon="i-far-xmark" />
        </app-button>
      </template>
    </app-input>

    <app-modal
      ref="modal"
      title="Filter Tanggal">
      <div class="flex max-sm:flex-col sm:justify-between gap-6">
        <div class="content">
          <datepicker-navigator
            v-model:view="view"
            v-model:month="month"
            v-model:year="year" />

          <view-months-in-year
            v-if="view === 'months'"
            v-model:view="view"
            v-model:month="month"
            :year="year" />
          <view-days-in-month
            v-else-if="view === 'days'"
            :month="month"
            :year="year">
            <template #default="{ date, gridColumnStart }">
              <app-button
                :style="{gridColumnStart}"
                :disabled="disabledDate?.(date)"
                class="disabled:text-gray-300"
                :class="{
                  'selected': modelInternal && isSameDay(date, modelInternal)
                }"
                @click="dateClicked(date)">
                {{ date.getDate() }}
              </app-button>
            </template>
          </view-days-in-month>

          <div class="flex items-center gap-8 justify-center mt-4">
            <div class="text-result text-xs sm:text-sm flex-center rounded">
              {{ stringifiedDate }}
            </div>

            <div class="select-time-range flex justify-center gap-4">
              <div class="flex rounded border gap-1 items-center px-3 text-sm">
                <select v-model="hour">
                  <option
                    v-for="i in 24"
                    :key="i"
                    :value="i-1">
                    {{ String(i-1).padStart(2, '0') }}
                  </option>
                </select>
                :
                <select v-model="minute">
                  <option
                    v-for="i in 60"
                    :key="i"
                    :value="i-1">
                    {{ String(i-1).padStart(2, '0') }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <app-modal-footer>
        <app-button
          class="btn-gray-outline mr-auto"
          @click="dateClicked(new Date())">
          Hari ini
        </app-button>
        <app-button
          class="btn-gray-outline"
          @click="close()">
          Tutup
        </app-button>
        <app-button
          class="btn-primary"
          @click="confirm()">
          Pilih
        </app-button>
      </app-modal-footer>
    </app-modal>
  </div>
</template>

<style scoped>
  @reference "@/assets/styles/main.css";

  .content {
    @apply text-center mx-auto;
    @apply min-w-72 max-w-96;
  }

  .selected {
    @apply text-white bg-primary-500 rounded-full;
  }
</style>
